import { IPDefaultButton, IPLabel, IPPrimaryButton, IPSeparator } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import Accordion from 'components/Accordion/Accordion';
import GenericModal from 'components/Dialogs/GenericModal';
import SelectListAndFolder from 'components/lists/SelectListAndFolder';
import TextInfo from 'components/Notification/Info';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanelFooter,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Joi from 'joi';
import TaskTypeFormElement, { FormFieldType, TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnString } from 'utils/sorting';

interface IFormFieldModalProps {
  field: TaskTypeFormElement;
  formElements: TaskTypeFormElement[];
  isOpen: boolean;
  isLoading: boolean;
  addMode: boolean;
  onDismiss: () => void;
  onAdd: (field: TaskTypeFormElement) => void;
  onUpdate: (field: TaskTypeFormElement) => void;
  // Fields surface renders this as a right-side panel (same chrome as FieldPanel) instead of a dialog.
  renderAsPanel?: boolean;
}

const getType = (field: TaskTypeFormElement): FormFieldType => {
  let type: FormFieldType;

  switch (field.fieldType) {
    case TaskTypeFieldType.AttachmentDocument:
    case TaskTypeFieldType.AttachmentListItem:
    case TaskTypeFieldType.AttachmentPage:
    case TaskTypeFieldType.AttachmentURL:
      type = FormFieldType.Attachment;
      break;
    case TaskTypeFieldType.ContextAsset:
    case TaskTypeFieldType.ContextControl:
    case TaskTypeFieldType.ContextObjective:
    case TaskTypeFieldType.ContextProcess:
    case TaskTypeFieldType.ContextRequirement:
    case TaskTypeFieldType.ContextRisk:
      type = FormFieldType.Context;
      break;
    case TaskTypeFieldType.KPI:
      type = FormFieldType.KPI;
      break;
  }

  return type;
};

const getCountOptions = (): IPSelectableOption[] => {
  const options: IPSelectableOption[] = [
    { key: 1, text: '1' },
    { key: 2, text: '2' },
    { key: 3, text: '3' },
    { key: 4, text: '4' },
    { key: 5, text: '5' },
    { key: 6, text: '6' },
    { key: 7, text: '7' },
    { key: 8, text: '8' },
    { key: 9, text: '9' },
    { key: 10, text: '10' },
  ];

  return options;
};

export const FormFieldModal = (props: IFormFieldModalProps) => {
  const { t } = useTranslation(['translation', 'forms']);
  const [field, setField] = useState<TaskTypeFormElement>(props.field.clone());
  const [type, setType] = useState<FormFieldType | undefined>(undefined);
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [typeError, setTypeError] = useState<string | undefined>(undefined);

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen && !props.isLoading && props.field) {
      const formFieldType = getType(props.field);
      setType(formFieldType);
      if (props.addMode) {
        setAvailableFieldType(formFieldType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.isLoading, props.field]);

  const setAvailableFieldType = (formFieldType: FormFieldType) => {
    let newType: TaskTypeFieldType = TaskTypeFieldType.ContextProcess;

    switch (formFieldType) {
      case FormFieldType.Attachment:
        newType = TaskTypeFieldType.AttachmentDocument;
        if (hasFieldType(TaskTypeFieldType.AttachmentDocument)) {
          newType = TaskTypeFieldType.AttachmentListItem;
        } else break;
        if (hasFieldType(TaskTypeFieldType.AttachmentListItem)) {
          newType = TaskTypeFieldType.AttachmentPage;
        } else break;
        if (hasFieldType(TaskTypeFieldType.AttachmentPage)) {
          newType = TaskTypeFieldType.AttachmentURL;
        } else break;
        if (hasFieldType(TaskTypeFieldType.AttachmentURL)) {
          setTypeError(t('forms:Dialogs.FieldForm.NoTypesAvailable'));
        }
        break;
      case FormFieldType.Context:
        newType = TaskTypeFieldType.ContextAsset;
        if (hasFieldType(TaskTypeFieldType.ContextAsset)) {
          newType = TaskTypeFieldType.ContextRisk;
        } else break;
        if (hasFieldType(TaskTypeFieldType.ContextRisk)) {
          newType = TaskTypeFieldType.ContextControl;
        } else break;
        if (hasFieldType(TaskTypeFieldType.ContextControl)) {
          newType = TaskTypeFieldType.ContextProcess;
        } else break;
        if (hasFieldType(TaskTypeFieldType.ContextProcess)) {
          newType = TaskTypeFieldType.ContextObjective;
        } else break;
        if (hasFieldType(TaskTypeFieldType.ContextObjective)) {
          newType = TaskTypeFieldType.ContextRequirement;
        }
        if (hasFieldType(TaskTypeFieldType.ContextRequirement)) {
          setTypeError(t('forms:Dialogs.FieldForm.NoTypesAvailable'));
        }
        break;
    }

    const newField = field.clone();
    newField.fieldType = newType;
    setField(newField);
  };

  const hasChanged = (): boolean => {
    if (props.addMode) {
      return field.name !== '';
    } else {
      return !field.isEqual(props.field);
    }
  };

  const disableSave = (): boolean => {
    return !hasChanged() || error || typeError || props.isLoading ? true : false;
  };

  const onSave = async () => {
    if (props.addMode) {
      props.onAdd(field);
    } else {
      props.onUpdate(field);
    }
  };

  const _localizedFields: Record<string, string> = {
    name: t('forms:Dialogs.FieldForm.Fields.Name'),
  };

  const getFieldError = (fieldName: string): string | undefined => {
    return error?.details?.find((e) => {
      return e.context?.key === fieldName;
    })?.message;
  };

  const validate = (fieldName: string, elm: TaskTypeFormElement): string => {
    const result = elm.validate(_localizedFields);
    setError(result.error);
    if (result.error) {
      const fieldError = result.error.details.find((e) => {
        return e.context?.key === fieldName;
      });

      if (fieldError) {
        return fieldError.message;
      }
    }

    if (fieldName === 'fieldType') {
    }

    return '';
  };

  const hasFieldType = (type: TaskTypeFieldType): boolean => {
    return props.formElements.some((f) => f.fieldType === type);
  };

  const getAttachmentTypeOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      {
        key: TaskTypeFieldType.AttachmentDocument,
        text: t('forms:Dialogs.FieldForm.AttachmentTypes.Document'),
        disabled: hasFieldType(TaskTypeFieldType.AttachmentDocument),
      },
      {
        key: TaskTypeFieldType.AttachmentListItem,
        text: t('forms:Dialogs.FieldForm.AttachmentTypes.ListItem'),
        disabled: hasFieldType(TaskTypeFieldType.AttachmentListItem),
      },
      {
        key: TaskTypeFieldType.AttachmentPage,
        text: t('forms:Dialogs.FieldForm.AttachmentTypes.Page'),
        disabled: hasFieldType(TaskTypeFieldType.AttachmentPage),
      },
      {
        key: TaskTypeFieldType.AttachmentURL,
        text: t('forms:Dialogs.FieldForm.AttachmentTypes.URL'),
        disabled: hasFieldType(TaskTypeFieldType.AttachmentURL),
      },
    ];

    return options;
  };

  const getContextTypeOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      {
        key: TaskTypeFieldType.ContextAsset,
        text: t('translation:General.EntityType.Asset'),
        disabled: hasFieldType(TaskTypeFieldType.ContextAsset),
      },
      {
        key: TaskTypeFieldType.ContextRisk,
        text: t('translation:General.EntityType.Risk'),
        disabled: hasFieldType(TaskTypeFieldType.ContextRisk),
      },
      {
        key: TaskTypeFieldType.ContextControl,
        text: t('translation:General.EntityType.Control'),
        disabled: hasFieldType(TaskTypeFieldType.ContextControl),
      },
      {
        key: TaskTypeFieldType.ContextProcess,
        text: t('translation:General.EntityType.Process'),
        disabled: hasFieldType(TaskTypeFieldType.ContextProcess),
      },
      {
        key: TaskTypeFieldType.ContextObjective,
        text: t('translation:General.EntityType.Objective'),
        disabled: hasFieldType(TaskTypeFieldType.ContextObjective),
      },
      {
        key: TaskTypeFieldType.ContextRequirement,
        text: t('translation:General.EntityType.Requirement'),
        disabled: hasFieldType(TaskTypeFieldType.ContextRequirement),
      },
    ];

    return options;
  };

  const getRequiredOptionalOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      { key: 1, text: t('forms:Dialogs.FieldForm.Fields.Required') },
      { key: -1, text: t('forms:Dialogs.FieldForm.Fields.Optional') },
    ];

    return options.sort((a, b) => sortOnString(a.text, b.text));
  };

  const setList = (listId: number | undefined, folderId: string | undefined) => {
    const newField = field.clone();
    newField.listId = listId;
    newField.folderId = folderId;
    setField(newField);
  };

  const getTitle = (): string => {
    if (props.addMode) {
      if (type === FormFieldType.Attachment) {
        return t('forms:Dialogs.FieldForm.Attachment.AddTitle');
      } else {
        return t('forms:Dialogs.FieldForm.Context.AddTitle');
      }
    } else {
      if (type === FormFieldType.Attachment) {
        return t('forms:Dialogs.FieldForm.Attachment.EditTitle');
      } else {
        return t('forms:Dialogs.FieldForm.Context.EditTitle');
      }
    }
  };

  //
  // Main render
  //
  const content = (
    <>
      <IPSeparator />
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <TextInfo message={t('forms:Dialogs.FieldForm.Fields.NameInfo')}>
            <IPLabel>{t('forms:Dialogs.FieldForm.Fields.Name')}</IPLabel>
          </TextInfo>
          <IPTextField
            required
            autoFocus
            value={field.name}
            placeholder={t('forms:Dialogs.FieldForm.Fields.NamePlaceholder')}
            onChange={(ev, newValue) => {
              const newField = field.clone();
              newField.name = newValue ?? '';
              validate('name', newField);
              setField(newField);
            }}
            maxLength={512}
            errorMessage={getFieldError('name')}
          />
        </IPStack.Item>
        {type === FormFieldType.Attachment && (
          <IPStack.Item>
            <TextInfo message={t('forms:Dialogs.FieldForm.Fields.AttachmentTypeInfo')}>
              <IPLabel>{t('forms:Dialogs.FieldForm.Fields.AttachmentType')}</IPLabel>
            </TextInfo>
            <IPDropdown
              options={getAttachmentTypeOptions()}
              selectedKey={field.fieldType}
              onChange={(ev, option) => {
                const newField = field.clone();
                newField.fieldType = option?.key as number;
                newField.listId = undefined;
                setField(newField);
              }}
              errorMessage={typeError}
            ></IPDropdown>
          </IPStack.Item>
        )}
        {type === FormFieldType.Context && (
          <IPStack.Item>
            <TextInfo message={t('forms:Dialogs.FieldForm.Fields.ContextTypeInfo')}>
              <IPLabel>{t('forms:Dialogs.FieldForm.Fields.ContextType')}</IPLabel>
            </TextInfo>
            <IPDropdown
              options={getContextTypeOptions()}
              selectedKey={field.fieldType}
              onChange={(ev, option) => {
                const newField = field.clone();
                newField.fieldType = option?.key as number;
                setField(newField);
              }}
              errorMessage={typeError}
            ></IPDropdown>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <TextInfo message={t('forms:Dialogs.FieldForm.Fields.CountInfo')}>
            <IPLabel>{t('forms:Dialogs.FieldForm.Fields.Count')}</IPLabel>
          </TextInfo>
          <IPStack horizontal tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPDropdown
                options={getRequiredOptionalOptions()}
                selectedKey={Math.sign(field.fieldMode ?? 1)}
                onChange={(ev, option) => {
                  const newField = field.clone();
                  newField.fieldMode = option?.key as number;
                  setField(newField);
                }}
                styles={{ root: { width: 100 } }}
              ></IPDropdown>
            </IPStack.Item>
            <IPStack.Item>
              <IPDropdown
                disabled={field.fieldMode === 0}
                options={getCountOptions()}
                selectedKey={Math.abs(field.fieldMode ?? 1)}
                onChange={(ev, option) => {
                  const nr = (option?.key as number) ?? 1;
                  const newField = field.clone();
                  newField.fieldMode = Math.sign(field.fieldMode ?? 1) * nr;
                  setField(newField);
                }}
                styles={{ root: { width: 50 } }}
              ></IPDropdown>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        {type === FormFieldType.Attachment && (
          <IPStack.Item>
            <SelectListAndFolder
              listId={field.listId}
              folderId={field.folderId}
              onUpdate={setList}
              allowNavigation={true}
            />
          </IPStack.Item>
        )}
        <Accordion title={t('forms:Dialogs.FieldForm.Fields.SectionTexts')} showBorder={true}>
          <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { padding: 10 } }}>
            <IPStack.Item>
              <TextInfo message={t('forms:Dialogs.FieldForm.Fields.DescriptionInfo')}>
                <IPLabel>{t('forms:Dialogs.FieldForm.Fields.Description')}</IPLabel>
              </TextInfo>
              <IPTextField
                value={field.description ?? ''}
                placeholder={t('forms:Dialogs.FieldForm.Fields.DescriptionPlaceholder')}
                onChange={(ev, newValue) => {
                  const newField = field.clone();
                  newField.description = newValue ?? '';
                  setField(newField);
                }}
                maxLength={1024}
              />
            </IPStack.Item>
            <IPStack.Item>
              <TextInfo message={t('forms:Dialogs.FieldForm.Fields.InstructionInfo')}>
                <IPLabel>{t('forms:Dialogs.FieldForm.Fields.Instruction')}</IPLabel>
              </TextInfo>
              <IPTextField
                value={field.instruction ?? ''}
                placeholder={t('forms:Dialogs.FieldForm.Fields.InstructionPlaceholder')}
                onChange={(ev, newValue) => {
                  const newField = field.clone();
                  newField.instruction = newValue ?? '';
                  setField(newField);
                }}
                maxLength={2048}
              />
            </IPStack.Item>
            <IPStack.Item>
              <TextInfo message={t('forms:Dialogs.FieldForm.Fields.ExpectedResultInfo')}>
                <IPLabel>{t('forms:Dialogs.FieldForm.Fields.ExpectedResult')}</IPLabel>
              </TextInfo>
              <IPTextField
                value={field.expectedResult ?? ''}
                placeholder={t('forms:Dialogs.FieldForm.Fields.ExpectedResultPlaceholder')}
                onChange={(ev, newValue) => {
                  const newField = field.clone();
                  newField.expectedResult = newValue ?? '';
                  setField(newField);
                }}
                maxLength={2048}
              />
            </IPStack.Item>
          </IPStack>
        </Accordion>
      </IPStack>
    </>
  );

  if (props.renderAsPanel) {
    return (
      <IPPanel
        headerText={getTitle()}
        type={IPPanelType.medium}
        isOpen={props.isOpen}
        isBlocking={true}
        isFooterAtBottom={true}
        onRenderFooterContent={() => (
          <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPPrimaryButton
                disabled={disableSave()}
                onClick={() => {
                  onSave();
                  props.onDismiss();
                }}
                text={t('translation:General.Button.Save')}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')} />
            </IPStack.Item>
          </IPStack>
        )}
        hasCloseButton={false}
        layerProps={globalLayerRightSideBarProps}
        focusTrapZoneProps={{ forceFocusInsideTrap: false }}
      >
        {content}
      </IPPanel>
    );
  }

  return (
    <GenericModal
      title={getTitle()}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      onSave={() => {
        onSave();
        props.onDismiss();
      }}
      saveDisabled={disableSave()}
    >
      {content}
    </GenericModal>
  );
};
